/**
 * web/src/screens/Settings/notifications.tsx — the Notifications section
 * (card HWrVePJ5qW4d slice 2; card MudceTexpbq1: moved verbatim out of
 * index.tsx). It owns its own subscription and preference state.
 */
import { useEffect, useState } from 'react';
import { api, isAbort } from '../../shell/bridge';
import { Switch } from '../../ui/Switch';
import { partnerLabel } from '../../lib/partnerName';

/* ── Notifications (card HWrVePJ5qW4d, follow-on slice 2) ──────────────
 * The preference surface the card asks for: one switch per trigger the
 * worker can fire. Preferences are per MEMBER (the same row drives every
 * device you own) and the defaults are the card's — question and partner
 * on, trial off.
 *
 * The switches alone would be a lie on a device that never subscribed, so
 * this section owns the subscription too: "Turn on" asks for permission,
 * subscribes with the worker's VAPID key and registers the device. The
 * prompt is user-initiated only (never on load), and a browser that cannot
 * do Web Push — insecure origin, no service worker, iOS Safari outside a
 * home-screen install — reads one plain sentence instead of controls that
 * would do nothing.
 *
 * What the payload will say is deliberately thin (see the worker): the
 * notification names an event, never your words, so the copy here promises
 * nothing about content. On iPhone the affordance is slice 3's install
 * steps: Web Push there exists only for a home-screen install, and Safari
 * never fires beforeinstallprompt, so instructions are the only honest
 * control (iosSafariNeedsInstall above decides when they show). */

type PushKind = 'question' | 'partner' | 'trial';
interface PushPrefs { question: boolean; partner: boolean; trial: boolean }

const PUSH_ROWS: { kind: PushKind; label: string; note: () => string }[] = [
  { kind: 'question', label: 'Daily question', note: () => 'When a new question lands for you both.' },
  { kind: 'partner', label: 'Partner activity', note: () => `When ${partnerLabel()} messages and you are not in the app.` },
  { kind: 'trial', label: 'Trial ending', note: () => 'A nudge before the free trial runs out. Off unless you want it.' },
];

/** base64url VAPID key → the Uint8Array PushManager.subscribe wants. */
function vapidKeyBytes(key: string): Uint8Array {
  const pad = '='.repeat((4 - (key.length % 4)) % 4);
  const b64 = (key + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Does this iPhone/iPad need an install before notifications can exist?
 *
 * iOS Safari exposes Web Push ONLY to a web app added to the home screen
 * (16.4+), and it never fires `beforeinstallprompt` — there is no button the
 * browser will show for us, so the only honest affordance is the three-step
 * Share → Add to Home Screen instruction. An already-installed app reads the
 * normal switches, so both `navigator.standalone` and the standalone display
 * mode are checked (iPadOS reports a desktop UA, hence the mode checks too).
 */
function iosSafariNeedsInstall(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
  } catch { /* a browser without matchMedia still needs the install */ }
  return true;
}

export function NotificationsSection() {
  const [state, setState] = useState<'loading' | 'unsupported' | 'off' | 'on'>('loading');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<PushPrefs | null>(null);
  const [busy, setBusy] = useState<'subscribe' | PushKind | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** True only when the ONE reason this device is incapable is a missing
   *  home-screen install (iOS) and the server is actually ready to serve
   *  push — otherwise the generic sentence stays, so the steps never point
   *  at an install that would still do nothing. */
  const [iosInstall, setIosInstall] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api<{ configured: boolean; publicKey: string | null }>('/api/push/config');
        const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
        // iOS may expose PushManager in a plain Safari tab, but subscribing
        // there fails — outside a home-screen install there is no service
        // worker scope Web Push accepts, so the install decides capability.
        const needsInstall = secure && iosSafariNeedsInstall();
        const capable = Boolean(cfg?.configured) && secure && !needsInstall
          && 'serviceWorker' in navigator && 'PushManager' in window;
        if (cancelled) return;
        if (!capable) {
          setIosInstall(Boolean(cfg?.configured) && needsInstall);
          setState('unsupported');
          return;
        }
        setPublicKey(cfg.publicKey);
        const p = await api<{ preferences: PushPrefs }>('/api/push/preferences');
        if (cancelled) return;
        setPrefs({
          question: Boolean(p?.preferences?.question),
          partner: Boolean(p?.preferences?.partner),
          trial: Boolean(p?.preferences?.trial),
        });
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (!cancelled) setState(sub ? 'on' : 'off');
      } catch (e) {
        // Card GuuZzFs7nVel — Settings teardown cancels /api/push/config and
        // /api/push/preferences; the nightly walk counted the teardown as a
        // page error on 2 of 3 runs with an identical stack. Silence it the
        // same way every other load in this file does (card d0Md65M0shSI).
        if (cancelled || isAbort(e)) return;
        console.error('[Pear] push preferences load failed', e);
        setState('unsupported');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = async () => {
    if (!publicKey) return;
    setBusy('subscribe');
    setNote(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState('off');
        setNote(permission === 'denied'
          ? 'Notifications are blocked for this site — turn them on in your browser settings, then try again.'
          : 'No problem — you can turn notifications on any time.');
        return;
      }
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      const sub = await reg.pushManager.getSubscription()
        ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKeyBytes(publicKey) as unknown as BufferSource });
      await api('/api/push/subscribe', { body: sub.toJSON() as unknown as Record<string, unknown> });
      setState('on');
    } catch (e) {
      if (isAbort(e)) return;
      console.error('[Pear] push subscribe failed', e);
      setNote('Couldn’t set up notifications on this device. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const setPref = async (kind: PushKind, on: boolean) => {
    setPrefs(prev => (prev ? { ...prev, [kind]: on } : prev));
    setBusy(kind);
    setNote(null);
    try {
      const p = await api<{ preferences: PushPrefs }>('/api/push/preferences', { method: 'PUT', body: { [kind]: on } });
      setPrefs({
        question: Boolean(p?.preferences?.question),
        partner: Boolean(p?.preferences?.partner),
        trial: Boolean(p?.preferences?.trial),
      });
    } catch (e) {
      if (isAbort(e)) return;
      console.error('[Pear] push preference save failed', e);
      setNote('Couldn’t save that. Try again.');
    } finally {
      setBusy(null);
    }
  };

  if (state === 'loading') return null;

  return (
    <section className="set-section" aria-labelledby="set-notifications-title" data-testid="push-notifications">
      <h3 className="set-section-title" id="set-notifications-title">
        Notifications
      </h3>
      {state === 'unsupported' ? (
        iosInstall ? (
          /* Slice 3: the iPhone affordance. Safari never fires
             beforeinstallprompt, so the install is instructions, not a button —
             and the last step lands back on this screen, where the switches
             appear the moment the app is on the home screen. */
          <div data-testid="push-ios-install">
            <p className="set-section-note">
              On iPhone, notifications come from the installed app — Safari tabs cannot send them:
            </p>
            <ol className="set-steps">
              <li>Tap the <b>Share</b> button in Safari.</li>
              <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
              <li>Open Pear from your home screen and come back here to switch notifications on.</li>
            </ol>
          </div>
        ) : (
          <p className="set-note">
            Notifications are not available on this device yet — Pear needs to be added to your home screen on iPhone, or opened in a supporting browser.
          </p>
        )
      ) : (
        <>
          <p className="set-section-note">
            Notifications say that something happened, never what you wrote — your lock screen stays yours.
          </p>
          {state === 'off' && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">This device</div>
                <div className="set-note">Turn on to get notified here when you are not in the app.</div>
              </div>
              <button
                type="button"
                className="set-row-btn"
                data-testid="push-turn-on"
                disabled={busy !== null}
                onClick={() => void subscribe()}
              >
                {busy === 'subscribe' ? 'Setting up…' : 'Turn on'}
              </button>
            </div>
          )}
          {state === 'on' && (
            <p className="set-note" data-testid="push-on-this-device">On for this device.</p>
          )}
          {PUSH_ROWS.map(row => (
            <div className="set-row" key={row.kind} data-testid={`push-${row.kind}-row`}>
              <div className="set-text">
                <div className="set-label">{row.label}</div>
                <div className="set-note">{row.note()}</div>
              </div>
              <Switch
                checked={Boolean(prefs?.[row.kind])}
                label={row.label}
                busy={busy === row.kind}
                disabled={busy !== null || prefs === null}
                onChange={on => void setPref(row.kind, on)}
              />
            </div>
          ))}
          {note && <p className="set-note" data-testid="push-note">{note}</p>}
        </>
      )}
    </section>
  );
}
